import { useMemo, useState } from 'react';
import {
  Box, Button, Container, Typography,
  Grid, Card, Chip,
  Stack,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Snackbar, Alert,
  InputAdornment, Slider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SendIcon from '@mui/icons-material/Send';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BoltIcon from '@mui/icons-material/Bolt';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TuneIcon from '@mui/icons-material/Tune';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import ElectricScooterIcon from '@mui/icons-material/ElectricScooter';
import EvStationIcon from '@mui/icons-material/EvStation';
import PublicIcon from '@mui/icons-material/Public';
import ShieldIcon from '@mui/icons-material/Shield';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SensorsIcon from '@mui/icons-material/Sensors';
import HubIcon from '@mui/icons-material/Hub';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

import {
  NAVY, NAVY_DARK, NAVY_LIGHT, GREEN, GREEN_DARK, GREEN_LIGHT, INK,
  PHONE, PHONE_DIGITS, SpeedSlashes, cutCorners, cutCornerTR,
} from './theme';
import {
  formatINR, computePlanDetails, FIXED_PLANS_CONFIG,
} from './roiCalc';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingActions from './components/FloatingActions';
import logo from './hero-img.png';

/* ══════════════════════════════════════════════════════
   HERO BANNER
══════════════════════════════════════════════════════ */
function HeroBanner() {
  return (
    <Box id="top" sx={{
      pt: { xs: 13, md: 17 }, pb: { xs: 9, md: 12 },
      bgcolor: NAVY_DARK,
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Ambient glow + accents */}
      <Box sx={{
        position: 'absolute', top: -160, right: -120, width: 480, height: 480,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha(GREEN, 0.22)} 0%, transparent 68%)`,
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: -160, left: -140, width: 420, height: 420,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha(NAVY_LIGHT, 0.5)} 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <SpeedSlashes color={GREEN} opacity={0.16} sx={{ top: 90, right: { xs: -20, md: 60 } }} />
      <SpeedSlashes color={GREEN} opacity={0.08} sx={{ bottom: 40, left: { xs: -30, md: 20 } }} />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Grid container spacing={{ xs: 6, md: 4 }} alignItems="center">

          {/* Left — copy */}
          <Grid size={{ xs: 12, md: 6.5 }}>
            <Chip
              icon={<VerifiedUserIcon sx={{ fontSize: 16, color: `${GREEN_LIGHT} !important` }} />}
              label="For Fleet Investors · Transparent Payouts"
              sx={{
                bgcolor: alpha(GREEN, 0.14), color: GREEN_LIGHT, fontWeight: 700, mb: 3, px: 1,
                border: `1px solid ${alpha(GREEN, 0.3)}`,
              }}
            />

            <Typography variant="h1" sx={{
              color: '#fff', fontSize: { xs: '2.3rem', sm: '2.9rem', md: '3.6rem' },
              lineHeight: 1.08, mb: 2.5,
            }}>
              Power India's EV Boom.{' '}
              <Box component="span" sx={{ color: GREEN_LIGHT }}>Earn ₹3,000 / Month</Box>{' '}
              per EV Unit.
            </Typography>

            <Typography variant="h6" sx={{
              color: 'rgba(255,255,255,0.72)', fontWeight: 400, lineHeight: 1.75,
              maxWidth: 540, mb: 4.5,
            }}>
              Invest in high-demand commercial electric two-wheelers with a fixed,
              transparent monthly return of <strong>₹3,000 per unit</strong>. Start with 1 Unit at ₹99,000,
              or scale to 10 & 25 Units with attractive fleet discounts up to 42.4% annual ROI.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 6 }}>
              <Button
                variant="contained" size="large" href="#roi-plans"
                startIcon={<TrendingUpIcon />}
                sx={{
                  bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DARK },
                  py: 1.8, px: 4, fontSize: '1rem', fontWeight: 700,
                  boxShadow: `0 10px 28px ${alpha(GREEN, 0.4)}`,
                  ...cutCornerTR(14),
                }}
              >
                View Investment Plans
              </Button>
              <Button
                variant="outlined" size="large" href="#cost-breakdown"
                startIcon={<ReceiptLongIcon />}
                sx={{
                  color: '#fff', borderColor: 'rgba(255,255,255,0.3)',
                  py: 1.8, px: 3.5, fontSize: '1rem',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                Cost Breakdown
              </Button>
            </Stack>

            {/* Stat strip */}
            <Grid container spacing={1.5} alignItems="stretch">
              {[
                { val: '₹99,000', lbl: 'Starting 1 Unit Cost' },
                { val: '₹3,000 / mo', lbl: 'Fixed Return / Unit' },
                { val: '36% – 42%', lbl: 'Annualised Yield' },
                { val: '218% – 254%', lbl: '6-Yr Total ROI (72 Mo)' },
              ].map(s => (
                <Grid size={{ xs: 6, sm: 3 }} key={s.lbl} sx={{ display: 'flex' }}>
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: { xs: 84, sm: 94 },
                    p: { xs: 1.5, sm: 1.75 },
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255,255,255,0.06)',
                    border: `1px solid ${alpha(GREEN, 0.25)}`,
                    borderTop: `3px solid ${GREEN}`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    boxSizing: 'border-box',
                    transition: 'transform 0.2s, background-color 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.09)',
                      borderColor: GREEN,
                    },
                  }}>
                    <Typography
                      sx={{
                        color: '#fff',
                        fontWeight: 800,
                        fontSize: { xs: '0.92rem', sm: '0.98rem', md: '1.08rem' },
                        fontFamily: '"Space Mono", monospace',
                        lineHeight: 1.2,
                        mb: 0.75,
                        whiteSpace: 'nowrap',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {s.val}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.68)',
                        fontWeight: 500,
                        fontSize: { xs: '0.68rem', sm: '0.72rem' },
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {s.lbl}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Right — brand visual */}
          <Grid size={{ xs: 12, md: 5.5 }} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{
              position: 'relative', height: 440,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Box sx={{
                width: 380, height: 380, borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(GREEN, 0.24)} 0%, transparent 72%)`,
                position: 'absolute',
              }} />
              <Box sx={{
                width: 320, height: 320, borderRadius: '50%',
                border: `1.5px solid ${alpha(GREEN, 0.35)}`,
                position: 'absolute',
              }} />
              <Box component="img" src={logo} alt="RideX City"
                sx={{ width: '92%', maxWidth: 420, position: 'relative', filter: 'drop-shadow(0 18px 34px rgba(0,0,0,0.45))' }} />

              {/* Floating credibility chips */}
              <Box sx={{
                position: 'absolute', top: 18, left: -10, px: 2, py: 1.25, borderRadius: 2,
                bgcolor: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <ShieldIcon sx={{ color: GREEN_DARK, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: INK, fontWeight: 700 }}>₹3,000 / mo Fixed Return</Typography>
              </Box>
              <Box sx={{
                position: 'absolute', bottom: 24, right: -14, px: 2, py: 1.25, borderRadius: 2,
                bgcolor: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <BoltIcon sx={{ color: GREEN_DARK, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: INK, fontWeight: 700 }}>Active EV Fleet</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

/* ══════════════════════════════════════════════════════
   WHY RIDEX CITY
══════════════════════════════════════════════════════ */
const WHY_POINTS = [
  {
    icon: <SecurityIcon />,
    title: 'Transparent Unit Costing',
    body: 'Complete clarity on every rupee: 75k Vehicle + 5k IoT + 8k Tech + 5k Battery + 6k Add-ons — zero hidden charges.',
  },
  {
    icon: <PaidIcon />,
    title: '₹3,000 / Month Fixed Returns',
    body: 'Every single EV unit earns a fixed monthly return of ₹3,000 (₹36,000 annually) credited directly to your account.',
  },
  {
    icon: <LocalOfferIcon />,
    title: 'Volume Fleet Discounts',
    body: 'Enjoy discounted per-unit pricing: ₹90,000/unit for 10 units and ₹85,000/unit for 25+ units, boosting your annual yield up to 42%.',
  },
  {
    icon: <PublicIcon />,
    title: 'Live IoT Telemetry & Security',
    body: 'Every EV is equipped with real-time GPS tracking, battery telemetry, geo-fencing, and commercial fleet management.',
  },
];

function WhySection() {
  return (
    <Box id="why" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#fff' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Chip label="WHY RIDEX CITY" sx={{ bgcolor: alpha(NAVY, 0.08), color: NAVY, mb: 2 }} />
          <Typography variant="h2" sx={{ color: NAVY, mb: 1.5, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            Built for Confident Investing
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 580, mx: 'auto', lineHeight: 1.75 }}>
            RideX City pairs India's fast-growing EV delivery & commute demand with a rock-solid,
            unit-level payout structure investors can verify.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {WHY_POINTS.map(pt => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={pt.title}>
              <Card elevation={0} sx={{
                height: '100%', p: 3.5, border: `1.5px solid ${alpha(NAVY, 0.1)}`,
                borderRadius: 3, transition: 'transform 0.25s, box-shadow 0.25s, border-color 0.25s',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 16px 40px ${alpha(NAVY, 0.12)}`, borderColor: alpha(GREEN, 0.4) },
              }}>
                <Box sx={{
                  width: 52, height: 52, borderRadius: 2, bgcolor: alpha(GREEN, 0.12),
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                  color: GREEN_DARK,
                }}>
                  {pt.icon}
                </Box>
                <Typography variant="h6" sx={{ color: NAVY, fontWeight: 800, mb: 1 }}>{pt.title}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{pt.body}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

/* ══════════════════════════════════════════════════════
   TRANSPARENT UNIT COST BREAKDOWN SECTION
══════════════════════════════════════════════════════ */
const UNIT_BREAKDOWN_ITEMS = [
  { item: 'Bike Cost', amount: 75000, short: '75k', desc: 'Commercial Grade Electric Vehicle (EV 2-Wheeler)', icon: <TwoWheelerIcon sx={{ fontSize: 26, color: NAVY }} /> },
  { item: 'IoT Cost', amount: 5000, short: '5k', desc: 'Smart GPS Telematics, 4G SIM, Remote Diagnostics & Anti-Theft', icon: <SensorsIcon sx={{ fontSize: 26, color: GREEN_DARK }} /> },
  { item: 'Integration Cost', amount: 8000, short: '8k', desc: 'RideX City OS Platform Setup & Rider Allocation Software', icon: <HubIcon sx={{ fontSize: 26, color: '#0288d1' }} /> },
  { item: 'Battery Cost', amount: 5000, short: '5k', desc: 'Certified Swappable Li-Ion Battery Pack / Deposit', icon: <BatteryChargingFullIcon sx={{ fontSize: 26, color: '#f57c00' }} /> },
  { item: 'Add-ons', amount: 6000, short: '6k', desc: 'Heavy-Duty Delivery Rack, Safety Crash Guard, Helmet Lock', icon: <BuildCircleIcon sx={{ fontSize: 26, color: '#7b1fa2' }} /> },
];

function CostBreakdownSection() {
  return (
    <Box id="cost-breakdown" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#f4f6fa' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip label="TRANSPARENT ECONOMICS" sx={{ bgcolor: alpha(GREEN, 0.15), color: GREEN_DARK, mb: 2, fontWeight: 700 }} />
          <Typography variant="h2" sx={{ color: NAVY, mb: 1.5, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            Per-Unit Cost Breakdown
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 620, mx: 'auto', lineHeight: 1.75 }}>
            Every single EV in the RideX City fleet is built with complete transparency.
            Here is the exact component-by-component investment structure:
          </Typography>
        </Box>

        {/* 5 Component Cards */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {UNIT_BREAKDOWN_ITEMS.map((item, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={item.item}>
              <Card elevation={0} sx={{
                p: 3, height: '100%', borderRadius: 3, border: `1.5px solid ${alpha(NAVY, 0.1)}`,
                bgcolor: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                transition: 'all 0.25s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 30px ${alpha(NAVY, 0.1)}`, borderColor: GREEN },
              }}>
                <Box>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: 2, bgcolor: alpha(NAVY, 0.05),
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                  }}>
                    {item.icon}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Part {idx + 1}
                  </Typography>
                  <Typography variant="h6" sx={{ color: NAVY, fontWeight: 800, mb: 0.5 }}>
                    {item.item}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem', lineHeight: 1.5, mb: 2 }}>
                    {item.desc}
                  </Typography>
                </Box>
                <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(NAVY, 0.08)}`, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Amount</Typography>
                  <Typography sx={{ color: GREEN_DARK, fontWeight: 800, fontSize: '1.25rem', fontFamily: '"Space Mono", monospace' }}>
                    {formatINR(item.amount)}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Total package highlight card */}
        <Box sx={{
          ...cutCorners(20),
          bgcolor: NAVY_DARK, color: '#fff', p: { xs: 3.5, md: 4.5 },
          display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 3,
          boxShadow: `0 16px 40px ${alpha(NAVY, 0.25)}`,
        }}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Chip label="TOTAL PACKAGE VALUE: ₹99,000" size="small" sx={{ bgcolor: alpha(GREEN, 0.2), color: GREEN_LIGHT, fontWeight: 700 }} />
              <Chip label="₹3,000 / MO RETURN" size="small" sx={{ bgcolor: alpha('#fff', 0.15), color: '#fff', fontWeight: 700 }} />
            </Stack>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', mb: 0.5 }}>
              1 Unit Standard Cost: ₹99,000
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 640 }}>
              75k (Bike Cost) + 5k (IoT Cost) + 8k (Integration Cost) + 5k (Battery Cost) + 6k (Add-ons) = ₹99,000 Complete Turnkey EV on Road.
              Investors get bulk discounts down to <strong>₹90,000 / unit (10 Units)</strong> and <strong>₹85,000 / unit (25 Units)</strong>.
            </Typography>
          </Box>
          <Button
            variant="contained" size="large" href="#roi-plans"
            startIcon={<TrendingUpIcon />}
            sx={{
              bgcolor: GREEN, color: '#fff', '&:hover': { bgcolor: GREEN_DARK },
              px: 3.5, py: 1.5, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
              boxShadow: `0 6px 20px ${alpha(GREEN, 0.4)}`,
            }}
          >
            Explore Plans Below
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

/* ══════════════════════════════════════════════════════
   FLEET GALLERY
══════════════════════════════════════════════════════ */
const FLEET_TILES = [
  { icon: <ElectricScooterIcon sx={{ fontSize: 40 }} />, title: 'City EV Two-Wheelers', hint: 'Commercial High-Efficiency Scooters' },
  { icon: <TwoWheelerIcon sx={{ fontSize: 40 }} />, title: 'Dedicated Delivery Riders', hint: 'Active on Top Delivery Platforms' },
  { icon: <EvStationIcon sx={{ fontSize: 40 }} />, title: 'Battery Swap Network', hint: 'Rapid 60-Second Battery Swapping' },
  { icon: <SensorsIcon sx={{ fontSize: 40 }} />, title: 'Live IoT Telemetry', hint: 'Real-time GPS, Anti-Theft & Health' },
];

function FleetGallery() {
  return (
    <Box id="fleet" sx={{ py: { xs: 8, md: 10 }, bgcolor: '#fff' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip label="OUR ACTIVE FLEET" sx={{ bgcolor: alpha(NAVY, 0.08), color: NAVY, mb: 2 }} />
          <Typography variant="h2" sx={{ color: NAVY, mb: 1.5, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            The Electric Fleet You're Investing In
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 560, mx: 'auto', lineHeight: 1.75 }}>
            Every vehicle in your portfolio is deployed on Indian city roads, earning daily revenue
            with certified rider onboarding.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {FLEET_TILES.map(tile => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={tile.title}>
              <Box sx={{
                ...cutCorners(20),
                position: 'relative', height: 220,
                background: `linear-gradient(155deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 1.5, overflow: 'hidden', p: 2.5,
                transition: 'transform 0.25s',
                '&:hover': { transform: 'translateY(-4px)' },
              }}>
                <SpeedSlashes color={GREEN} opacity={0.18} sx={{ top: -10, right: -30 }} />
                <Box sx={{
                  width: 62, height: 62, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: GREEN_LIGHT,
                }}>
                  {tile.icon}
                </Box>
                <Typography variant="body1" sx={{ color: '#fff', fontWeight: 700, textAlign: 'center', px: 1 }}>
                  {tile.title}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', fontSize: '0.75rem' }}>
                  {tile.hint}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

/* ══════════════════════════════════════════════════════
   ROI PLANS SECTION
   - 1 Unit: ₹99,000
   - 10 Units: ₹9,00,000 (90k / unit)
   - 25 Units: ₹21,25,000 (85k / unit)
   - Custom: Tiered pricing (1-9 @ 99k, 10-24 @ 90k, 25+ @ 85k)
   - ₹3,000 / month return for all units
   - Payback: 72 Months (6 Years)
══════════════════════════════════════════════════════ */
const CUSTOM_PRESET_UNITS = [1, 5, 10, 15, 20, 25, 50, 100];

const OFFICIAL_PLANS_SUMMARY = [
  { plan: '1 UNIT', units: 1, totalInv: 99000, monthlyReturn: 3000, paybackMonths: 72, roi6Yr: '218%', annualisedReturn: '36%', color: '#1b8e2d', lightBg: 'rgba(27, 142, 45, 0.08)' },
  { plan: '10 UNITS', units: 10, totalInv: 900000, monthlyReturn: 30000, paybackMonths: 72, roi6Yr: '240%', annualisedReturn: '40%', color: '#0b2a5c', lightBg: 'rgba(11, 42, 92, 0.08)' },
  { plan: '25 UNITS', units: 25, totalInv: 2125000, monthlyReturn: 75000, paybackMonths: 72, roi6Yr: '254%', annualisedReturn: '42%', color: '#e65100', lightBg: 'rgba(230, 81, 0, 0.08)' },
];

function OfficialFlyerOverview() {
  return (
    <Box sx={{ mb: 6 }}>
      {/* Returns & Payback Table + Highlight Callout */}
      <Grid container spacing={3} alignItems="stretch">
        
        {/* Table Column */}
        <Grid size={{ xs: 12, lg: 7.5 }}>
          <Card elevation={0} sx={{
            border: `1.5px solid ${alpha(NAVY, 0.15)}`, borderRadius: 3, overflow: 'hidden', height: '100%',
            boxShadow: '0 8px 24px rgba(11,42,92,0.06)',
          }}>
            <Box sx={{
              bgcolor: NAVY_DARK, color: '#fff', px: 3, py: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
                RETURNS & PAYBACK MATRIX (72 MONTHS)
              </Typography>
              <Chip label="72 Months (6 Years)" size="small" sx={{ bgcolor: alpha(GREEN, 0.25), color: GREEN_LIGHT, fontWeight: 700 }} />
            </Box>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(NAVY, 0.04) }}>
                    <TableCell sx={{ fontWeight: 800, color: NAVY, fontSize: '0.78rem', py: 1.6 }}>Investment</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, color: NAVY, fontSize: '0.78rem', py: 1.6 }}>Return p.m</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, color: NAVY, fontSize: '0.78rem', py: 1.6 }}>Investment Period</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, color: NAVY, fontSize: '0.78rem', py: 1.6 }}>ROI (6 Yrs)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: NAVY, fontSize: '0.78rem', py: 1.6 }}>Annualised Return</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {OFFICIAL_PLANS_SUMMARY.map(p => (
                    <TableRow key={p.plan} sx={{ '&:hover': { bgcolor: p.lightBg } }}>
                      <TableCell sx={{ py: 1.6 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: NAVY, fontFamily: '"Space Mono",monospace' }}>
                          {formatINR(p.totalInv)}
                        </Typography>
                        <Chip
                          label={p.plan}
                          size="small"
                          sx={{
                            bgcolor: p.color,
                            color: '#fff',
                            fontWeight: 800,
                            fontSize: '0.68rem',
                            height: 18,
                            mt: 0.3,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: GREEN_DARK, fontFamily: '"Space Mono",monospace', fontSize: '0.92rem' }}>
                        {formatINR(p.monthlyReturn)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.82rem' }}>
                        {p.paybackMonths} Months
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 900, color: p.color, fontSize: '1rem', fontFamily: '"Space Mono",monospace' }}>
                          {p.roi6Yr}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, color: GREEN_DARK, fontSize: '0.96rem', fontFamily: '"Space Mono",monospace' }}>
                        {p.annualisedReturn}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Highlight Callout Box matching flyer */}
        <Grid size={{ xs: 12, lg: 4.5 }}>
          <Box sx={{
            ...cutCornerTR(18),
            bgcolor: NAVY_DARK, color: '#fff',
            p: { xs: 3.5, md: 4 }, height: '100%',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            boxShadow: `0 12px 30px ${alpha(NAVY, 0.25)}`,
            border: `1.5px solid ${alpha(GREEN, 0.4)}`,
            position: 'relative', overflow: 'hidden',
          }}>
            <SpeedSlashes color={GREEN} opacity={0.12} sx={{ top: -10, right: -20 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: 2.5, bgcolor: alpha(GREEN, 0.2),
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: GREEN_LIGHT,
                border: `1px solid ${alpha(GREEN, 0.4)}`,
              }}>
                <TrendingUpIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography sx={{ color: GREEN_LIGHT, fontWeight: 900, fontSize: { xs: '1.6rem', sm: '1.9rem' }, fontFamily: '"Space Mono",monospace', lineHeight: 1 }}>
                  ₹3,000
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  MONTHLY RETURN PER UNIT
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.75, fontSize: '0.88rem' }}>
              You will earn <strong>₹3,000 every month</strong> for each bike deployed in the fleet.
              These returns are additional to your investment and continue throughout the <strong>72-month contract period</strong> without any deductions or conditions.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

function PlanDetailView({ planDetails, planTitle }) {
  const {
    units, ratePerUnit, totalInvestment, savings,
    monthlyReturn, annualReturn, return36Months, return60Months, return72Months,
    annualRoiPct, total6YrRoiPct, costBreakdownRows
  } = planDetails;

  return (
    <Box sx={{ mt: 4 }}>
      {/* Top Banner / Key Metrics Summary */}
      <Box sx={{
        ...cutCornerTR(18),
        bgcolor: alpha(GREEN, 0.08), border: `1.5px solid ${alpha(GREEN, 0.35)}`,
        p: { xs: 3, md: 4 }, mb: 3.5,
        display: 'flex', flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 3,
      }}>
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Chip label="SELECTED FLEET BREAKDOWN" size="small" sx={{ bgcolor: alpha(GREEN, 0.2), color: GREEN_DARK, fontWeight: 700 }} />
            {savings > 0 && (
              <Chip label={`Bulk Savings: ${formatINR(savings)}`} size="small" sx={{ bgcolor: alpha(NAVY, 0.1), color: NAVY, fontWeight: 700 }} />
            )}
          </Stack>
          <Typography variant="h4" sx={{ color: NAVY, fontWeight: 800, mb: 0.5 }}>
            {planTitle} — {units} EV {units === 1 ? 'Unit' : 'Units'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Price per Unit: <strong>{formatINR(ratePerUnit)}</strong> · Total Investment:{' '}
            <strong style={{ color: NAVY }}>{formatINR(totalInvestment)}</strong>
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Box sx={{
            bgcolor: '#fff', borderRadius: 3, px: 3.5, py: 2, textAlign: 'center',
            boxShadow: `0 6px 20px ${alpha(NAVY, 0.08)}`, border: `1px solid ${alpha(NAVY, 0.08)}`,
          }}>
            <Typography sx={{ color: GREEN_DARK, fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.2rem' }, lineHeight: 1, fontFamily: '"Space Mono",monospace' }}>
              {formatINR(monthlyReturn)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Monthly Payout (₹3k × {units})
            </Typography>
          </Box>
          <Box sx={{
            bgcolor: '#fff', borderRadius: 3, px: 3.5, py: 2, textAlign: 'center',
            boxShadow: `0 6px 20px ${alpha(NAVY, 0.08)}`, border: `1px solid ${alpha(GREEN, 0.25)}`,
          }}>
            <Typography sx={{ color: NAVY, fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.2rem' }, lineHeight: 1, fontFamily: '"Space Mono",monospace' }}>
              {total6YrRoiPct}%
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              6-Yr Total ROI (72 Mo)
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Grid: Left (Cost Breakdown Table) · Right (Returns Projection Ladder) */}
      <Grid container spacing={3} sx={{ mb: 3.5 }}>
        
        {/* Left: Component Cost Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={{ border: `1.5px solid ${alpha(NAVY, 0.15)}`, borderRadius: 3, overflow: 'hidden', height: '100%' }}>
            <Box sx={{
              bgcolor: alpha(NAVY, 0.06), borderBottom: `3px solid ${NAVY}`,
              px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              <ShoppingCartIcon sx={{ color: NAVY, fontSize: 22 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ color: NAVY, fontWeight: 800, lineHeight: 1.2 }}>
                  Capital Deployment Breakdown ({units} {units === 1 ? 'Unit' : 'Units'})
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Effective unit rate: {formatINR(ratePerUnit)} per vehicle
                </Typography>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: NAVY, fontSize: '0.78rem', py: 1.3 }}>Component</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: NAVY, fontSize: '0.78rem', py: 1.3 }}>Base Rate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: NAVY, fontSize: '0.78rem', py: 1.3 }}>Total ({units}x)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costBreakdownRows.map((r) => (
                    <TableRow key={r.item} sx={{ '&:hover': { bgcolor: alpha(NAVY, 0.02) } }}>
                      <TableCell sx={{ py: 1.4 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.84rem', color: INK }}>{r.item}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', display: 'block' }}>{r.desc}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
                        {formatINR(r.rate)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.84rem', fontFamily: '"Space Mono",monospace', color: INK }}>
                        {formatINR(r.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: alpha(NAVY, 0.07) }}>
                    <TableCell sx={{ fontWeight: 800, color: NAVY, fontSize: '0.88rem', py: 1.6 }}>
                      Total Investment
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: NAVY, fontSize: '0.82rem' }}>
                      {units} {units === 1 ? 'Unit' : 'Units'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 900, color: NAVY, fontSize: '1rem', fontFamily: '"Space Mono",monospace' }}>
                        {formatINR(totalInvestment)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Right: Return & Payout Projections */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={{ border: `1.5px solid ${alpha(GREEN, 0.35)}`, borderRadius: 3, overflow: 'hidden', height: '100%' }}>
            <Box sx={{
              bgcolor: alpha(GREEN, 0.1), borderBottom: `3px solid ${GREEN}`,
              px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              <PaidIcon sx={{ color: GREEN_DARK, fontSize: 22 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ color: GREEN_DARK, fontWeight: 800, lineHeight: 1.2 }}>
                  Fixed Payout Schedule (₹3,000 / mo / unit)
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Direct monthly bank deposits without deductions
                </Typography>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: GREEN_DARK, fontSize: '0.78rem', py: 1.3 }}>Payout Milestone</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: GREEN_DARK, fontSize: '0.78rem', py: 1.3 }}>Frequency / Unit</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: GREEN_DARK, fontSize: '0.78rem', py: 1.3 }}>Total Returns</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { milestone: 'Monthly Payout', freq: `₹3,000 × ${units}`, total: monthlyReturn, highlight: false },
                    { milestone: '1 Year (12 Months)', freq: 'Annual Return', total: annualReturn, highlight: true, note: `${annualRoiPct.toFixed(1)}% Annual Yield` },
                    { milestone: '2 Years (24 Months)', freq: '24-Month Return', total: annualReturn * 2, highlight: false },
                    { milestone: '3 Years (36 Months)', freq: '36-Month Return', total: return36Months, highlight: false },
                    { milestone: '5 Years (60 Months)', freq: '60-Month Return', total: return60Months, highlight: false },
                    { milestone: '6 Years (72 Months)', freq: 'Full Contract Period', total: return72Months, highlight: true, note: `${total6YrRoiPct}% 6-Yr Return` },
                  ].map(p => (
                    <TableRow key={p.milestone} sx={{ bgcolor: p.highlight ? alpha(GREEN, 0.04) : 'inherit', '&:hover': { bgcolor: alpha(GREEN, 0.08) } }}>
                      <TableCell sx={{ py: 1.4 }}>
                        <Typography sx={{ fontWeight: p.highlight ? 700 : 600, fontSize: '0.84rem', color: INK }}>
                          {p.milestone}
                        </Typography>
                        {p.note && (
                          <Chip label={p.note} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(GREEN, 0.18), color: GREEN_DARK, mt: 0.3 }} />
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
                        {p.freq}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.88rem', fontFamily: '"Space Mono",monospace', color: GREEN_DARK }}>
                        {formatINR(p.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Net Return Bottom Highlight Box */}
      <Box sx={{
        ...cutCorners(20),
        bgcolor: alpha(GREEN, 0.09), border: `1.5px solid ${alpha(GREEN, 0.3)}`,
        p: { xs: 3, md: 4 }, textAlign: 'center',
      }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
          <TrendingUpIcon sx={{ color: GREEN_DARK, fontSize: 22 }} />
          <Typography variant="overline" sx={{ color: GREEN_DARK, fontWeight: 800, fontSize: '0.85rem' }}>
            Summary of Returns
          </Typography>
        </Stack>
        <Typography sx={{
          color: GREEN_DARK, fontWeight: 900, fontFamily: '"Space Mono",monospace',
          fontSize: { xs: '2rem', md: '2.5rem' }, lineHeight: 1.1, mb: 1,
        }}>
          {formatINR(monthlyReturn)} <Box component="span" sx={{ fontSize: '1.2rem', color: 'text.secondary', fontWeight: 600 }}>/ Month</Box>
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 680, mx: 'auto', lineHeight: 1.7 }}>
          Earn a predictable <strong style={{ color: GREEN_DARK }}>{formatINR(annualReturn)} every year</strong> on your{' '}
          {formatINR(totalInvestment)} investment ({units} {units === 1 ? 'Unit' : 'Units'} @ {formatINR(ratePerUnit)}/unit).
          Enjoy a solid <strong>{total6YrRoiPct}% total return over the 72-month contract period</strong> with complete asset backing.
        </Typography>
      </Box>
    </Box>
  );
}

function RoiPlansSection() {
  const [selectedPlanId, setSelectedPlanId] = useState(1); // 1 = 1 unit, 2 = 10 units, 3 = 25 units, 4 = custom
  const [customUnits, setCustomUnits] = useState(15);

  const activeUnits = useMemo(() => {
    if (selectedPlanId === 1) return 1;
    if (selectedPlanId === 2) return 10;
    if (selectedPlanId === 3) return 25;
    return Math.max(1, customUnits);
  }, [selectedPlanId, customUnits]);

  const planDetails = useMemo(() => computePlanDetails(activeUnits), [activeUnits]);

  const planTitle = useMemo(() => {
    if (selectedPlanId === 1) return 'Plan 1 · Starter Fleet (1 Unit)';
    if (selectedPlanId === 2) return 'Plan 2 · Growth Fleet (10 Units)';
    if (selectedPlanId === 3) return 'Plan 3 · Enterprise Fleet (25 Units)';
    return 'Plan 4 · Custom Fleet';
  }, [selectedPlanId]);

  return (
    <Box id="roi-plans" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#fff' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip label="INVESTMENT PLANS & RETURNS" sx={{ bgcolor: alpha(GREEN, 0.15), color: GREEN_DARK, mb: 2, fontWeight: 700 }} />
          <Typography variant="h2" sx={{ color: NAVY, mb: 1.5, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            ROI (Return on Investment)
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 640, mx: 'auto', lineHeight: 1.75 }}>
            Every plan guarantees a fixed <strong>₹3,000 monthly payout per EV unit</strong> across a <strong>72-month contract period</strong>.
            Select a standard tier or calculate your custom fleet scale.
          </Typography>
        </Box>

        {/* Official Flyer Matrix and Callout */}
        <OfficialFlyerOverview />

        {/* 4 Plan Cards Grid */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {FIXED_PLANS_CONFIG.map(p => {
            const active = selectedPlanId === p.id;
            const details = computePlanDetails(p.units);
            return (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={p.id}>
                <Box
                  onClick={() => setSelectedPlanId(p.id)}
                  sx={{
                    ...cutCornerTR(16),
                    cursor: 'pointer', p: 3, height: '100%',
                    position: 'relative',
                    border: `2px solid ${active ? GREEN : alpha(NAVY, 0.12)}`,
                    bgcolor: active ? alpha(GREEN, 0.06) : '#fff',
                    boxShadow: active ? `0 12px 32px ${alpha(GREEN, 0.2)}` : 'none',
                    transition: 'all 0.25s',
                    '&:hover': { borderColor: GREEN, transform: 'translateY(-3px)' },
                  }}
                >
                  {p.badge && (
                    <Chip
                      label={p.badge}
                      size="small"
                      sx={{
                        position: 'absolute', top: 12, right: 12,
                        bgcolor: active ? GREEN : alpha(NAVY, 0.08),
                        color: active ? '#fff' : NAVY,
                        fontSize: '0.65rem', fontWeight: 800, height: 22,
                      }}
                    />
                  )}

                  <Box sx={{
                    width: 44, height: 44, borderRadius: '50%', mb: 2,
                    bgcolor: active ? alpha(GREEN, 0.2) : alpha(NAVY, 0.06),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: active ? GREEN_DARK : NAVY,
                  }}>
                    {p.id === 1 && <StorefrontIcon />}
                    {p.id === 2 && <TwoWheelerIcon />}
                    {p.id === 3 && <AccountBalanceIcon />}
                  </Box>

                  <Typography variant="overline" sx={{ color: active ? GREEN_DARK : 'text.secondary', fontWeight: 700, display: 'block', mb: 0.25 }}>
                    Plan {p.id} · {p.units} {p.units === 1 ? 'Unit' : 'Units'}
                  </Typography>

                  <Typography variant="h5" sx={{ color: NAVY, fontWeight: 900, fontFamily: '"Space Mono",monospace', fontSize: '1.35rem', mb: 0.5 }}>
                    {formatINR(p.amount)}
                  </Typography>

                  <Typography variant="body2" sx={{ color: GREEN_DARK, fontWeight: 700, mb: 1 }}>
                    ₹3,000 × {p.units} = {formatINR(details.monthlyReturn)} / mo
                  </Typography>

                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', borderTop: `1px solid ${alpha(NAVY, 0.08)}`, pt: 1 }}>
                    Price: <strong>{formatINR(p.ratePerUnit)} / unit</strong> · <strong>{p.sixYearReturnPct} 6-Yr Return</strong>
                  </Typography>
                </Box>
              </Grid>
            );
          })}

          {/* Plan 4: Custom */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              onClick={() => setSelectedPlanId(4)}
              sx={{
                ...cutCornerTR(16),
                cursor: 'pointer', p: 3, height: '100%',
                position: 'relative',
                border: `2px solid ${selectedPlanId === 4 ? GREEN : alpha(NAVY, 0.12)}`,
                bgcolor: selectedPlanId === 4 ? alpha(GREEN, 0.06) : '#fff',
                boxShadow: selectedPlanId === 4 ? `0 12px 32px ${alpha(GREEN, 0.2)}` : 'none',
                transition: 'all 0.25s',
                '&:hover': { borderColor: GREEN, transform: 'translateY(-3px)' },
              }}
            >
              <Chip
                label="Custom Scale"
                size="small"
                sx={{
                  position: 'absolute', top: 12, right: 12,
                  bgcolor: selectedPlanId === 4 ? GREEN : alpha(NAVY, 0.08),
                  color: selectedPlanId === 4 ? '#fff' : NAVY,
                  fontSize: '0.65rem', fontWeight: 800, height: 22,
                }}
              />

              <Box sx={{
                width: 44, height: 44, borderRadius: '50%', mb: 2,
                bgcolor: selectedPlanId === 4 ? alpha(GREEN, 0.2) : alpha(NAVY, 0.06),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: selectedPlanId === 4 ? GREEN_DARK : NAVY,
              }}>
                <TuneIcon />
              </Box>

              <Typography variant="overline" sx={{ color: selectedPlanId === 4 ? GREEN_DARK : 'text.secondary', fontWeight: 700, display: 'block', mb: 0.25 }}>
                Plan 4 · Custom
              </Typography>

              <Typography variant="h5" sx={{ color: NAVY, fontWeight: 900, fontSize: '1.35rem', mb: 0.5 }}>
                Choose Units
              </Typography>

              <Typography variant="body2" sx={{ color: GREEN_DARK, fontWeight: 700, mb: 1 }}>
                Tiered Bulk Discounts
              </Typography>

              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', borderTop: `1px solid ${alpha(NAVY, 0.08)}`, pt: 1 }}>
                1-9 @ 99k · 10-24 @ 90k · 25+ @ 85k
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Custom Plan Slider / Input Controller (Visible when Plan 4 selected) */}
        {selectedPlanId === 4 && (
          <Card elevation={0} sx={{
            border: `1.5px solid ${alpha(GREEN, 0.35)}`, bgcolor: alpha(GREEN, 0.04),
            p: { xs: 3, md: 4 }, mb: 4, borderRadius: 3,
          }}>
            <Typography variant="h6" sx={{ color: NAVY, fontWeight: 800, mb: 0.5 }}>
              Custom Fleet Size Calculator
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Select the number of electric vehicle units to see the real-time tiered investment, monthly payouts, and returns.
            </Typography>

            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  fullWidth
                  label="Number of EV Units"
                  type="number"
                  value={customUnits}
                  onChange={(e) => {
                    const val = Math.max(1, Math.min(200, Number(e.target.value) || 1));
                    setCustomUnits(val);
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">Units</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 7 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, mb: 1, display: 'block' }}>
                  Quick Presets:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {CUSTOM_PRESET_UNITS.map(u => (
                    <Chip
                      key={u}
                      label={`${u} ${u === 1 ? 'Unit' : 'Units'}`}
                      onClick={() => setCustomUnits(u)}
                      sx={{
                        bgcolor: customUnits === u ? GREEN : alpha(NAVY, 0.07),
                        color: customUnits === u ? '#fff' : NAVY,
                        cursor: 'pointer', fontWeight: 700,
                        '&:hover': { bgcolor: customUnits === u ? GREEN_DARK : alpha(NAVY, 0.14) },
                      }}
                    />
                  ))}
                </Stack>
              </Grid>

              <Grid size={12}>
                <Box sx={{ px: 1, pt: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                    Adjust Fleet Scale (1 to 100 Units):
                  </Typography>
                  <Slider
                    value={customUnits}
                    min={1}
                    max={100}
                    step={1}
                    onChange={(e, val) => setCustomUnits(val)}
                    valueLabelDisplay="auto"
                    sx={{
                      color: GREEN,
                      '& .MuiSlider-thumb': {
                        bgcolor: '#fff',
                        border: `2px solid ${GREEN_DARK}`,
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'text.secondary', fontSize: '0.75rem' }}>
                    <span>1 Unit (₹99k/unit)</span>
                    <span>10 Units (₹90k/unit)</span>
                    <span>25+ Units (₹85k/unit)</span>
                    <span>100 Units</span>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>
        )}

        {/* Detailed Plan breakdown & Projection table */}
        <PlanDetailView planDetails={planDetails} planTitle={planTitle} />

        {/* Disclaimer / Compliance banner */}
        <Box sx={{
          mt: 4, p: 2.5, borderRadius: 3, bgcolor: alpha(NAVY, 0.04),
          border: `1px dashed ${alpha(NAVY, 0.2)}`, display: 'flex', gap: 1.5, alignItems: 'flex-start',
        }}>
          <SecurityIcon sx={{ color: NAVY, fontSize: 20, mt: 0.2, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <strong style={{ color: NAVY }}>Note: </strong>
            All investments are backed by tangible electric two-wheeler assets, registered commercial telemetry,
            and RideX City's active fleet operations. For custom fleet enterprise arrangements or specific city allocations,
            please reach out to our investor desk.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

/* ══════════════════════════════════════════════════════
   CONTACT SECTION
══════════════════════════════════════════════════════ */
function ContactSection() {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const submit = e => {
    e.preventDefault();
    const msg = `Hi RideX City, I am ${form.name}. Phone: ${form.phone}. ${form.message}`;
    window.open(`https://wa.me/${PHONE_DIGITS}?text=${encodeURIComponent(msg)}`, '_blank');
    setSent(true);
    setForm({ name: '', phone: '', message: '' });
  };

  return (
    <Box id="contact" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#f4f6fa' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Chip label="GET IN TOUCH" sx={{ bgcolor: alpha(NAVY, 0.08), color: NAVY, mb: 2 }} />
          <Typography variant="h2" sx={{ color: NAVY, mb: 1.5, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            Ready to Invest in RideX City?
          </Typography>
          <Typography variant="h6" sx={{
            color: 'text.secondary', fontWeight: 400,
            maxWidth: 480, mx: 'auto', lineHeight: 1.75
          }}>
            Reach out to lock in your EV allocation. Our investment relations team responds within 24 hours.
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="stretch">

          {/* Left – contact info */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{
              height: '100%', p: { xs: 3.5, md: 5 }, borderRadius: 4,
              bgcolor: NAVY_DARK,
              display: 'flex', flexDirection: 'column', gap: 4,
              position: 'relative', overflow: 'hidden',
            }}>
              <Box sx={{
                position: 'absolute', top: -70, right: -70, width: 220, height: 220,
                borderRadius: '50%', border: `2px solid ${alpha(GREEN, 0.2)}`, pointerEvents: 'none'
              }} />
              <SpeedSlashes color={GREEN} opacity={0.14} sx={{ bottom: -10, left: -20 }} />

              <Box>
                <Typography variant="h4" sx={{ color: '#fff', mb: 1.5 }}>Let's Talk</Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.8 }}>
                  Contact us to discuss your vehicle allocation, review agreements, and start earning ₹3,000/month per unit.
                </Typography>
              </Box>

              {[
                {
                  bg: 'rgba(255,255,255,0.08)', icon: <PhoneIcon sx={{ color: GREEN_LIGHT }} />,
                  lbl: 'Call Us Directly', val: PHONE, href: `tel:${PHONE}`,
                },
                {
                  bg: alpha('#25D366', 0.16), icon: <WhatsAppIcon sx={{ color: '#3ee07f' }} />,
                  lbl: 'WhatsApp Instant Chat', val: PHONE,
                  href: `https://wa.me/${PHONE_DIGITS}?text=Hi%2C%20I%20want%20to%20invest%20in%20RideX%20City%20EV%20Fleet`,
                  external: true,
                },
                {
                  bg: alpha(GREEN, 0.16), icon: <TrendingUpIcon sx={{ color: GREEN_LIGHT }} />,
                  lbl: 'Official Website', val: 'www.ridexcity.com',
                  href: 'https://www.ridexcity.com', external: true,
                },
              ].map(c => (
                <Box key={c.lbl} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 50, height: 50, borderRadius: '50%', bgcolor: c.bg, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {c.icon}
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block' }}>
                      {c.lbl}
                    </Typography>
                    <Typography variant="h6" component="a" href={c.href}
                      target={c.external ? '_blank' : undefined}
                      rel={c.external ? 'noopener noreferrer' : undefined}
                      sx={{
                        color: '#fff', textDecoration: 'none', fontWeight: 700,
                        '&:hover': { color: GREEN_LIGHT }, transition: 'color 0.2s'
                      }}>
                      {c.val}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Right – enquiry form */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Card elevation={0} sx={{
              height: '100%', border: `1.5px solid ${alpha(NAVY, 0.13)}`,
              p: { xs: 3, md: 4.5 }, borderRadius: 4,
            }}>
              <Typography variant="h5" sx={{ color: NAVY, mb: 3, fontWeight: 700 }}>
                Send an Investor Enquiry
              </Typography>
              <Box component="form" onSubmit={submit}>
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Your Name" name="name" required
                      value={form.name} onChange={handle} placeholder="e.g. Rajesh Kumar" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Phone Number" name="phone" required
                      value={form.phone} onChange={handle} placeholder="10-digit number"
                      inputProps={{ inputMode: 'tel', pattern: '[0-9]{10}', maxLength: 10 }} />
                  </Grid>
                  <Grid size={12}>
                    <TextField fullWidth label="Message / Interested Fleet Size" name="message" multiline rows={4}
                      value={form.message} onChange={handle}
                      placeholder="I am interested in investing in 10 EV Units (₹9,00,000 plan) with ₹30,000 monthly returns..." />
                  </Grid>
                  <Grid size={12}>
                    <Button type="submit" variant="contained" size="large" fullWidth
                      endIcon={<SendIcon />}
                      sx={{
                        bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DARK }, py: 1.8, fontSize: '1rem',
                        fontWeight: 700, boxShadow: `0 6px 20px ${alpha(GREEN, 0.4)}`
                      }}>
                      Send via WhatsApp
                    </Button>
                  </Grid>
                  <Grid size={12}>
                    <Button variant="outlined" size="large" fullWidth
                      href={`tel:${PHONE}`} startIcon={<PhoneIcon />}
                      sx={{
                        borderColor: NAVY, color: NAVY, py: 1.6,
                        '&:hover': { bgcolor: alpha(NAVY, 0.05), borderColor: NAVY }
                      }}>
                      Or Call Directly: {PHONE}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Snackbar open={sent} autoHideDuration={4000} onClose={() => setSent(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSent(false)}>
          Message opened in WhatsApp! We'll reply shortly.
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ══════════════════════════════════════════════════════
   HOME PAGE ROOT
══════════════════════════════════════════════════════ */
function HomePage() {
  return (
    <Box sx={{ bgcolor: 'background.default', overflowX: 'hidden' }}>
      <Navbar />
      <HeroBanner />
      <WhySection />
      <CostBreakdownSection />
      <FleetGallery />
      <RoiPlansSection />
      <ContactSection />
      <Footer />
      <FloatingActions />
    </Box>
  );
}

export default HomePage;
